import { useMemo, useState } from 'react';
import { format, parseISO, isValid, isBefore, isAfter, startOfDay } from 'date-fns';
import { CalendarDays, Clock, ChevronDown, ChevronUp, X, Sparkles } from 'lucide-react';
import { useStayContext, useDuringStayMode } from '@/context/StayContext';
import { cn } from '@/lib/utils';

interface StayDateHeaderProps {
  className?: string;
  /** Show compact version (single line) */
  compact?: boolean;
  /** Show date picker for changing dates */
  showDatePicker?: boolean;
  /** Callback when dates are changed via picker */
  onDatesChange?: (startDate: string | null, endDate: string | null) => void;
}

/**
 * Header component for "during-stay" mode
 * Shows stay dates, countdown, and optionally a date picker
 */
export function StayDateHeader({
  className,
  compact = false,
  showDatePicker = false,
  onDatesChange,
}: StayDateHeaderProps) {
  const { isDuringStayMode, daysUntilStayEnds, stayStartDate, stayEndDate } = useDuringStayMode();
  const { setStayDates, clearStayDates, mode, stayDates } = useStayContext();
  const [isExpanded, setIsExpanded] = useState(false);

  // Format dates for display
  const formattedDates = useMemo(() => {
    if (!stayStartDate || !stayEndDate) return null;

    const start = parseISO(stayStartDate);
    const end = parseISO(stayEndDate);

    if (!isValid(start) || !isValid(end)) return null;

    // Same month format: "Mar 15-20, 2024"
    // Different month format: "Mar 15 - Apr 2, 2024"
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${format(start, 'MMM d')}-${format(end, 'd, yyyy')}`;
    }
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
  }, [stayStartDate, stayEndDate]);

  // Get countdown message
  const countdownMessage = useMemo(() => {
    if (daysUntilStayEnds === null) return null;

    if (daysUntilStayEnds === 0) {
      return "Last day to book!";
    }
    if (daysUntilStayEnds === 1) {
      return "1 day left to book!";
    }
    if (daysUntilStayEnds <= 3) {
      return `${daysUntilStayEnds} days left to book!`;
    }
    return `${daysUntilStayEnds} days until checkout`;
  }, [daysUntilStayEnds]);

  // Get stay status
  const stayStatus = useMemo(() => {
    if (!stayStartDate || !stayEndDate) return 'none';

    const today = startOfDay(new Date());
    const start = parseISO(stayStartDate);
    const end = parseISO(stayEndDate);

    if (isBefore(today, start)) return 'upcoming';
    if (isAfter(today, end)) return 'past';
    return 'current';
  }, [stayStartDate, stayEndDate]);

  // Don't render if not in during-stay mode or no valid dates
  if (!isDuringStayMode && mode !== 'during-stay') {
    return null;
  }

  // If we have the mode but no dates, show a message
  if (mode === 'during-stay' && !stayStartDate) {
    return (
      <div className={cn(
        "rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30 p-4",
        className
      )}>
        <div className="flex items-center gap-2 text-muted-foreground">
          <CalendarDays className="h-5 w-5" />
          <p className="text-sm">
            Set your stay dates to see available experiences
          </p>
        </div>
      </div>
    );
  }

  // Compact version - single line
  if (compact) {
    return (
      <div className={cn(
        "flex items-center justify-between gap-4 rounded-lg bg-primary/10 px-4 py-2",
        className
      )}>
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Your stay: {formattedDates}</span>
        </div>
        {countdownMessage && daysUntilStayEnds !== null && daysUntilStayEnds <= 7 && (
          <span className={cn(
            "text-xs font-semibold px-2 py-0.5 rounded-full",
            daysUntilStayEnds <= 1 ? "bg-destructive/20 text-destructive" : "bg-amber-500/20 text-amber-700"
          )}>
            {countdownMessage}
          </span>
        )}
      </div>
    );
  }

  // Full version
  return (
    <div className={cn(
      "rounded-lg border bg-gradient-to-r from-primary/5 to-primary/10 overflow-hidden",
      className
    )}>
      {/* Main header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold text-foreground">
                Experiences during your stay
              </h2>
            </div>
            
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4" />
                <span>{formattedDates}</span>
              </div>
              
              {stayStatus === 'current' && countdownMessage && (
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  <span className={cn(
                    "font-medium",
                    daysUntilStayEnds !== null && daysUntilStayEnds <= 1 && "text-destructive"
                  )}>
                    {countdownMessage}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {showDatePicker && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
              >
                <span>Change dates</span>
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
            )}
            
            <button
              onClick={() => {
                clearStayDates();
                onDatesChange?.(null, null);
              }}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
              aria-label="Clear stay dates"
              title="Clear stay dates and browse all experiences"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Urgency badge for upcoming/current stays */}
        {daysUntilStayEnds !== null && daysUntilStayEnds <= 3 && stayStatus !== 'past' && (
          <div className="mt-3">
            <span className={cn(
              "inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full",
              daysUntilStayEnds === 0
                ? "bg-destructive text-destructive-foreground"
                : daysUntilStayEnds === 1
                  ? "bg-destructive/80 text-destructive-foreground"
                  : "bg-amber-500 text-white"
            )}>
              <Clock className="h-3 w-3" />
              {daysUntilStayEnds === 0
                ? "Book now - checking out today!"
                : daysUntilStayEnds === 1
                  ? "Only 1 day left!"
                  : `Only ${daysUntilStayEnds} days left!`
              }
            </span>
          </div>
        )}
      </div>

      {/* Expandable date picker */}
      {showDatePicker && isExpanded && (
        <StayDatePicker
          startDate={stayDates.startDate}
          endDate={stayDates.endDate}
          onDatesChange={(start, end) => {
            setStayDates(start, end);
            onDatesChange?.(start, end);
          }}
          onClose={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
}

interface StayDatePickerProps {
  startDate: string | null;
  endDate: string | null;
  onDatesChange: (startDate: string | null, endDate: string | null) => void;
  onClose?: () => void;
}

/**
 * Simple date picker for changing stay dates
 */
function StayDatePicker({ startDate, endDate, onDatesChange, onClose }: StayDatePickerProps) {
  const [localStart, setLocalStart] = useState(startDate || '');
  const [localEnd, setLocalEnd] = useState(endDate || '');

  const handleApply = () => {
    onDatesChange(localStart || null, localEnd || null);
    onClose?.();
  };

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 90);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  return (
    <div className="border-t bg-background p-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[140px]">
          <label htmlFor="stay-start" className="block text-xs font-medium text-muted-foreground mb-1">
            Check-in
          </label>
          <input
            id="stay-start"
            type="date"
            value={localStart}
            onChange={(e) => setLocalStart(e.target.value)}
            min={today}
            max={maxDateStr}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          />
        </div>
        
        <div className="flex-1 min-w-[140px]">
          <label htmlFor="stay-end" className="block text-xs font-medium text-muted-foreground mb-1">
            Check-out
          </label>
          <input
            id="stay-end"
            type="date"
            value={localEnd}
            onChange={(e) => setLocalEnd(e.target.value)}
            min={localStart || today}
            max={maxDateStr}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleApply}
            disabled={!localStart || !localEnd}
            className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Apply
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export { StayDatePicker };
